import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import ExoForm from '../exo/ExoForm';
import ExoAlert from '../exo/ExoAlert';
import { API_PATCH, API_POST, DBRequest, useConfig } from '../../api/api';

const OAUTH_DRIVERS = ['dropbox', 'onedrive'];

/**
 * First step of the network storage wizard. Collects connection information
 * (or starts an OAuth flow) and persists the storage so the second step can
 * browse its folder structure. For non-OAuth drivers the connection must be
 * tested successfully before the user can continue.
 */
export const StepConnection = ({
  dataCollection,
  updateDataCollection,
  setActiveStep,
  onClose,
}) => {
  const { t } = useTranslation();
  const config = useConfig();

  const [driver, setDriver] = useState(dataCollection.driver || 'smb');
  const [formData, setFormData] = useState(dataCollection || {});
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [connectionOk, setConnectionOk] = useState(false);
  const [alert, setAlert] = useState(null);

  const isOAuth = OAUTH_DRIVERS.includes(driver);
  const isEdit = Boolean(dataCollection.id);

  const initialFields = [
    {
      label: t('Driver'),
      key: 'driver',
      type: 'customSelect',
      default: 'smb',
      noUnselect: true,
      options: [
        { label: 'SMB', value: 'smb' },
        { label: 'FTP', value: 'ftp' },
        { label: 'SFTP', value: 'sftp' },
        { label: 'Dropbox', value: 'dropbox' },
        { label: 'OneDrive', value: 'onedrive' },
      ],
    },
    {
      label: t('Displayname'),
      key: 'name',
      type: 'text',
      info: t('A name to identify this storage within the app.'),
    },
  ];

  const credentialFields = [
    {
      label: t('Host'),
      key: 'host',
      type: 'text',
      info: t(
        'The address of the server, for example an IP address (192.168.1.10) or a domain (files.example.com).',
      ),
    },
    {
      label: t('Username'),
      key: 'username',
      type: 'text',
      info: t('The username of the account used to log into the server.'),
    },
    {
      label: t('Password'),
      key: 'password',
      type: 'password',
      info: t('The password of the account used to log into the server.'),
    },
  ];

  const sshFields = [
    {
      label: t('SSH Private Key'),
      key: 'sshPrivateKey',
      type: 'text',
      multiline: true,
      info: t(
        'The private SSH key for key-based authentication. Paste the entire key including the BEGIN and END lines.',
      ),
    },
    {
      label: t('SSH Key Password'),
      key: 'sshKeyPassword',
      type: 'text',
      info: t('The passphrase that protects the SSH private key, if one is set.'),
    },
  ];

  const fieldMap = {
    smb: [...initialFields, ...credentialFields],
    ftp: [...initialFields, ...credentialFields],
    sftp: [...initialFields, ...credentialFields, ...sshFields],
    dropbox: [...initialFields],
    onedrive: [...initialFields],
  };

  function handleChange(data) {
    setFormData(data);
    if (data?.driver && data.driver !== driver) setDriver(data.driver);
    // Any change to the credentials invalidates a prior successful test.
    setConnectionOk(false);
  }

  function showError(err) {
    setAlert({
      message: err?.message || t('Connection Failed'),
      cancelLabel: t('Close'),
    });
  }

  function handleTest() {
    setConnectionOk(false);
    DBRequest({
      config,
      path: 'network-storages/test-connection',
      method: API_POST(formData),
      onLoading: setIsTesting,
      onResponse: () => {
        setConnectionOk(true);
        setAlert(null);
      },
      onError: showError,
    });
  }

  function handleContinue() {
    if (isOAuth) connectOAuth();
    else persistConnection();
  }

  function connectOAuth() {
    const payload = { name: formData.name, redirectUrl: window.location.href };
    const base = driver === 'dropbox' ? 'dropbox' : 'onedrive';
    const path = formData.id ? `${base}/update/${formData.id}` : `${base}/create`;

    DBRequest({
      config,
      path,
      method: formData.id ? API_PATCH(payload) : API_POST(payload),
      onLoading: setIsLoading,
      onResponse: (res) => {
        const storage = res.data?.networkStorage;
        // Marker so the settings page can resume at step 2 after the
        // OAuth provider redirects the browser back.
        localStorage.setItem(
          'networkStorageSetup',
          JSON.stringify({ id: storage?.id, driver }),
        );
        window.location.assign(res.data.authUrl);
      },
      onError: showError,
    });
  }

  function persistConnection() {
    const payload = { driver, name: formData.name };
    ['host', 'username', 'workgroup'].forEach((key) => {
      if (formData[key]) payload[key] = formData[key];
    });
    // Only send credentials when the user actually entered them, so editing
    // a storage without re-typing the password does not wipe it.
    ['password', 'sshPrivateKey', 'sshKeyPassword'].forEach((key) => {
      if (formData[key]) payload[key] = formData[key];
    });

    DBRequest({
      config,
      path: formData.id ? `network-storages/${formData.id}` : 'network-storages',
      method: formData.id ? API_PATCH(payload) : API_POST(payload),
      onLoading: setIsLoading,
      onResponse: (res) => {
        updateDataCollection({ ...formData, id: res.data.id, driver });
        setActiveStep(1);
      },
      onError: showError,
    });
  }

  // Non-OAuth: continue only after a successful test (or when editing an
  // already-connected storage). OAuth: a name is enough to start the flow.
  const canContinue = isOAuth
    ? Boolean(formData?.name)
    : connectionOk || isEdit;

  const continueLabel = isOAuth
    ? isEdit
      ? t('Re-run OAuth')
      : driver === 'dropbox'
        ? t('Connect to Dropbox')
        : t('Connect to OneDrive')
    : t('Continue');

  return (
    <Box className="flex flex-col h-full w-full gap-3">
      <Box className="flex-1 min-h-0">
        <ExoForm
          fields={fieldMap[driver] || fieldMap.smb}
          startDataCollection={dataCollection}
          onChange={handleChange}
          noControls
          noDifferences
          noEnter
        />
      </Box>

      {!isOAuth && connectionOk && (
        <Box
          className="flex items-center gap-1"
          sx={{ color: 'success.main' }}
        >
          <CheckCircleIcon fontSize="small" />
          <Typography variant="body2">{t('Connection successful')}</Typography>
        </Box>
      )}

      <Box className="flex flex-row w-full justify-between items-center">
        <Button onClick={onClose} variant="outlined" color="error">
          {t('Cancel')}
        </Button>
        <Box className="flex gap-2">
          {!isOAuth && (
            <Button
              variant="outlined"
              onClick={handleTest}
              disabled={isTesting || isLoading}
            >
              {isTesting
                ? t('Checking Connection') + '...'
                : t('Test Connection')}
            </Button>
          )}
          <Button
            variant="contained"
            color="success"
            onClick={handleContinue}
            disabled={isLoading || isTesting || !canContinue}
          >
            {continueLabel}
          </Button>
        </Box>
      </Box>

      <ExoAlert
        open={Boolean(alert)}
        alert={alert || {}}
        onClose={() => setAlert(null)}
      />
    </Box>
  );
};
